import React, { useMemo } from "react";
import { Card, Row, Col, Table, Progress, Badge, Typography, Spin, Button, Alert } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useGetQuestionCoverageQuery } from "../../services/nestjs-base-api";
import { useAuth } from "../../hooks/useAuth";
import {
    ICoverageStatement,
    ICoverageCategory,
} from "../../services/nestjs-base-api/types";

const { Title, Text } = Typography;

interface QuestionCoverageDashboardProps {
    surveyId: number;
}

const QuestionCoverageDashboard: React.FC<QuestionCoverageDashboardProps> = ({ surveyId }) => {
    const intl = useIntl();
    const { user } = useAuth();

    const {
        data: coverageData,
        isLoading,
        error,
        refetch
    } = useGetQuestionCoverageQuery({
        jwtToken: user?.jwtToken || "",
        surveyId
    });

    const getBalanceColor = (score: number): string => {
        if (score >= 85) return '#52c41a';
        if (score >= 70) return '#1890ff';
        if (score >= 50) return '#faad14';
        return '#ff4d4f';
    };

    // Category response balance chart
    const categoryBalanceChartOptions: Highcharts.Options = useMemo(() => {
        if (!coverageData?.categories) return {};
        const cats = coverageData.categories;
        const target = coverageData.summary?.targetResponsesPerCategory || 0;

        return {
            chart: { type: 'bar', height: Math.max(300, cats.length * 40) },
            title: { text: intl.formatMessage({ id: 'coverage.category.balance', defaultMessage: 'Responses by Question Category' }) },
            xAxis: {
                categories: cats.map(c => c.category),
                title: { text: null },
            },
            yAxis: {
                min: 0,
                title: { text: intl.formatMessage({ id: 'coverage.responses', defaultMessage: 'Total Responses' }) },
                plotLines: target > 0 ? [{
                    value: target,
                    color: '#1890ff',
                    dashStyle: 'Dash',
                    width: 2,
                    label: {
                        text: `${intl.formatMessage({ id: 'coverage.target', defaultMessage: 'Target' })} (${target})`,
                        align: 'left',
                        x: 5,
                        y: -5,
                        rotation: 0,
                        style: { color: '#1890ff', fontSize: '11px' },
                    },
                    zIndex: 3,
                }] : [],
            },
            tooltip: {
                formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                    const cat = cats[this.point.index];
                    return `<b>${cat.category}</b><br/>` +
                        `${intl.formatMessage({ id: 'coverage.responses', defaultMessage: 'Total Responses' })}: <b>${cat.totalResponses}</b><br/>` +
                        `${intl.formatMessage({ id: 'coverage.statements', defaultMessage: 'Statements' })}: ${cat.statementCount}`;
                },
            },
            legend: { enabled: false },
            plotOptions: {
                bar: {
                    dataLabels: { enabled: true, format: '{y}' },
                },
            },
            series: [{
                name: intl.formatMessage({ id: 'coverage.responses', defaultMessage: 'Total Responses' }),
                type: 'bar',
                data: cats.map(c => {
                    const absDiff = Math.abs(c.totalResponses - target);
                    const color = target <= 0 ? '#1890ff'
                        : target >= 10
                            ? (absDiff / target <= 0.15 ? '#52c41a' : absDiff / target <= 0.3 ? '#1890ff' : '#faad14')
                            : (absDiff <= 1 ? '#52c41a' : absDiff <= 2 ? '#1890ff' : '#faad14');
                    return { y: c.totalResponses, color };
                }),
            }],
        };
    }, [coverageData, intl]);

    // Within-category statement balance chart (only categories with multiple non-core statements)
    const statementBalanceChartOptions: Highcharts.Options = useMemo(() => {
        if (!coverageData?.categories) return {};

        const multiStatementCats = coverageData.categories.filter(
            c => c.statements.filter(s => !s.isCore).length > 1
        );

        if (multiStatementCats.length === 0) return {};

        // Build grouped series: each statement is a data point within its category
        const allStatements: { category: string; code: string; responses: number }[] = [];
        for (const cat of multiStatementCats) {
            const nonCore = cat.statements.filter(s => !s.isCore);
            for (const stmt of nonCore) {
                allStatements.push({
                    category: cat.category,
                    code: stmt.questionCode,
                    responses: stmt.responseCount,
                });
            }
        }

        const labels = allStatements.map(s => s.code);

        // Color by category
        const categoryColors: Record<string, string> = {};
        const palette = ['#5B8FF9', '#5AD8A6', '#F6BD16', '#E86452', '#6DC8EC', '#945FB9', '#FF9845', '#1E9493', '#FF99C3', '#7262FD'];
        multiStatementCats.forEach((cat, i) => {
            categoryColors[cat.category] = palette[i % palette.length];
        });

        return {
            chart: { type: 'column', height: 400 },
            title: { text: intl.formatMessage({ id: 'coverage.statement.balance', defaultMessage: 'Statement Balance Within Categories' }) },
            subtitle: { text: intl.formatMessage({ id: 'coverage.statement.balance.subtitle', defaultMessage: 'Categories with multiple statements' }) },
            xAxis: {
                categories: labels,
                title: { text: null },
                labels: {
                    rotation: -45,
                    style: { fontSize: '11px' },
                    formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                        const idx = typeof this.pos === 'number' ? this.pos : 0;
                        const stmt = allStatements[idx];
                        const color = stmt ? (categoryColors[stmt.category] || '#333') : '#333';
                        return `<span style="color:${color}">${this.value}</span>`;
                    },
                    useHTML: true,
                },
            },
            yAxis: {
                min: 0,
                title: { text: intl.formatMessage({ id: 'coverage.responses', defaultMessage: 'Total Responses' }) },
            },
            tooltip: {
                formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                    const idx = this.point.index;
                    const stmt = allStatements[idx];
                    return `<b>${stmt.code}</b> (${stmt.category})<br/>` +
                        `${intl.formatMessage({ id: 'coverage.responses', defaultMessage: 'Total Responses' })}: <b>${stmt.responses}</b>`;
                },
            },
            legend: { enabled: false },
            plotOptions: {
                column: {
                    dataLabels: { enabled: true, format: '{y}' },
                },
            },
            series: [{
                name: intl.formatMessage({ id: 'coverage.responses', defaultMessage: 'Total Responses' }),
                type: 'column',
                data: allStatements.map(s => ({
                    y: s.responses,
                    color: categoryColors[s.category] || '#1890ff',
                })),
            }],
        };
    }, [coverageData, intl]);

    // Category table with expandable statement rows
    const categoryColumns = [
        {
            title: intl.formatMessage({ id: 'coverage.category', defaultMessage: 'Question Category' }),
            dataIndex: 'category',
            key: 'category',
            sorter: (a: ICoverageCategory, b: ICoverageCategory) => a.category.localeCompare(b.category),
        },
        {
            title: intl.formatMessage({ id: 'coverage.statement.count', defaultMessage: 'Statements' }),
            dataIndex: 'statementCount',
            key: 'statementCount',
            width: 120,
            sorter: (a: ICoverageCategory, b: ICoverageCategory) => a.statementCount - b.statementCount,
        },
        {
            title: intl.formatMessage({ id: 'coverage.total.responses', defaultMessage: 'Total Responses' }),
            dataIndex: 'totalResponses',
            key: 'totalResponses',
            width: 150,
            sorter: (a: ICoverageCategory, b: ICoverageCategory) => a.totalResponses - b.totalResponses,
        },
        {
            title: intl.formatMessage({ id: 'coverage.vs.target', defaultMessage: 'vs Target' }),
            key: 'vsTarget',
            width: 150,
            render: (record: ICoverageCategory) => {
                const target = coverageData?.summary?.targetResponsesPerCategory || 0;
                if (target === 0) return '-';
                const diff = record.totalResponses - target;
                const absDiff = Math.abs(diff);
                const pct = Math.round((diff / target) * 100);
                // Use absolute diff for color when target is small, percentage when target is large
                const color = target >= 10
                    ? (Math.abs(pct) <= 15 ? '#52c41a' : Math.abs(pct) <= 30 ? '#1890ff' : '#faad14')
                    : (absDiff <= 1 ? '#52c41a' : absDiff <= 2 ? '#1890ff' : '#faad14');
                return (
                    <Text style={{ color }}>
                        {diff >= 0 ? '+' : ''}{diff} ({pct >= 0 ? '+' : ''}{pct}%)
                    </Text>
                );
            },
        },
    ];

    const statementColumns = [
        {
            title: intl.formatMessage({ id: 'coverage.code', defaultMessage: 'Code' }),
            dataIndex: 'questionCode',
            key: 'questionCode',
            width: 100,
        },
        {
            title: intl.formatMessage({ id: 'coverage.statement.text', defaultMessage: 'Statement' }),
            dataIndex: 'questionText',
            key: 'questionText',
            ellipsis: true,
        },
        {
            title: intl.formatMessage({ id: 'coverage.type', defaultMessage: 'Type' }),
            key: 'type',
            width: 100,
            render: (record: ICoverageStatement) => (
                <Badge
                    color={record.isCore ? '#52c41a' : '#d9d9d9'}
                    text={record.isCore
                        ? intl.formatMessage({ id: 'coverage.core', defaultMessage: 'Core' })
                        : intl.formatMessage({ id: 'coverage.rotated', defaultMessage: 'Rotated' })
                    }
                />
            ),
        },
        {
            title: intl.formatMessage({ id: 'coverage.responses.col', defaultMessage: 'Responses' }),
            dataIndex: 'responseCount',
            key: 'responseCount',
            width: 120,
            sorter: (a: ICoverageStatement, b: ICoverageStatement) => a.responseCount - b.responseCount,
        },
    ];

    const expandedRowRender = (category: ICoverageCategory) => {
        return (
            <Table
                columns={statementColumns}
                dataSource={category.statements}
                rowKey="questionId"
                pagination={false}
                size="small"
            />
        );
    };

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text>{intl.formatMessage({ id: 'loading.coverage', defaultMessage: 'Loading coverage data...' })}</Text>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message={intl.formatMessage({ id: 'error.title', defaultMessage: 'Error' })}
                description={intl.formatMessage({ id: 'error.coverage.load', defaultMessage: 'Failed to load coverage data' })}
                type="error"
                showIcon
                action={
                    <Button size="small" danger onClick={() => refetch()}>
                        {intl.formatMessage({ id: 'button.retry', defaultMessage: 'Retry' })}
                    </Button>
                }
            />
        );
    }

    if (!coverageData) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Text>{intl.formatMessage({ id: 'loading.coverage', defaultMessage: 'Loading coverage data...' })}</Text>
            </div>
        );
    }

    const hasMultiStatementCategories = (coverageData.categories || []).some(
        c => c.statements.filter(s => !s.isCore).length > 1
    );

    return (
        <div>
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => refetch()}
                >
                    {intl.formatMessage({ id: 'button.refresh', defaultMessage: 'Refresh' })}
                </Button>
            </div>

            {/* Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                                {coverageData.summary?.totalStatements || 0}
                            </Title>
                            <Text type="secondary">
                                {intl.formatMessage({ id: 'coverage.total.statements', defaultMessage: 'Total Statements' })}
                            </Text>
                            <div style={{ marginTop: 4 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {coverageData.summary?.coreStatements || 0} {intl.formatMessage({ id: 'coverage.core', defaultMessage: 'Core' })} / {coverageData.summary?.nonCoreStatements || 0} {intl.formatMessage({ id: 'coverage.rotated', defaultMessage: 'Rotated' })}
                                </Text>
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                                {coverageData.summary?.completedResponses || 0}
                            </Title>
                            <Text type="secondary">
                                {intl.formatMessage({ id: 'coverage.completed.responses', defaultMessage: 'Completed Responses' })}
                            </Text>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Progress
                                type="circle"
                                percent={coverageData.summary?.categoryBalanceScore || 0}
                                size={80}
                                strokeColor={getBalanceColor(coverageData.summary?.categoryBalanceScore || 0)}
                            />
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">
                                    {intl.formatMessage({ id: 'coverage.category.balance.score', defaultMessage: 'Category Balance' })}
                                </Text>
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Progress
                                type="circle"
                                percent={coverageData.summary?.statementBalanceScore || 0}
                                size={80}
                                strokeColor={getBalanceColor(coverageData.summary?.statementBalanceScore || 0)}
                            />
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">
                                    {intl.formatMessage({ id: 'coverage.statement.balance.score', defaultMessage: 'Statement Balance' })}
                                </Text>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Charts */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={hasMultiStatementCategories ? 12 : 24}>
                    <Card>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={categoryBalanceChartOptions}
                        />
                    </Card>
                </Col>
                {hasMultiStatementCategories && (
                    <Col xs={24} lg={12}>
                        <Card>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={statementBalanceChartOptions}
                            />
                        </Card>
                    </Col>
                )}
            </Row>

            {/* Category Table with Expandable Statements */}
            <Card title={intl.formatMessage({ id: 'coverage.category.detail', defaultMessage: 'Category Coverage Detail' })}>
                <Table
                    columns={categoryColumns}
                    dataSource={coverageData.categories || []}
                    rowKey="category"
                    expandable={{
                        expandedRowRender,
                        defaultExpandAllRows: false,
                    }}
                    pagination={false}
                    size="small"
                />
            </Card>
        </div>
    );
};

export default QuestionCoverageDashboard;
